import { useEffect, useState } from 'react';
import { Activity, ChevronDown, ChevronRight, Folder, FolderLock, FolderOpen, Inbox, Search, Trash2, Users } from 'lucide-react';
import type { SharedSpace, SpaceNodeView, SpaceSummary } from '../../lib/api';
import { formatBytes, isNodeDrag, type FolderTree } from '../../lib/spaces';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

export type SpacesView = 'folder' | 'trash' | 'activity' | 'search' | 'requests';

/**
 * Accepting a dragged row as a move into this folder. Shared by the tree's rows and the listing's,
 * so a folder is a drop target wherever it is drawn.
 */
export function useMoveTarget(folderId: string, dragging: SpaceNodeView | null, onDropNode: (folderId: string) => void) {
  const [over, setOver] = useState(false);
  // A folder into itself or into the folder it already sits in is no move at all.
  const accepts = dragging !== null && dragging.id !== folderId && dragging.parentId !== folderId;
  return {
    over: over && accepts,
    props: {
      onDragOver: (e: React.DragEvent) => {
        if (!accepts || !isNodeDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      },
      // `dragleave` also fires on crossing into a child element of the same row; only leaving the row counts.
      onDragLeave: (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      },
      onDrop: (e: React.DragEvent) => {
        setOver(false);
        if (!accepts || !isNodeDrag(e)) return;
        e.preventDefault();
        // Stops the listing underneath from also reading this as a drop of files to upload.
        e.stopPropagation();
        onDropNode(folderId);
      },
    },
  };
}

function TreeRow({
  id, name, depth, tree, activeId, onOpen, dragging, onDropNode, trailing,
}: {
  id: string;
  name: string;
  depth: number;
  tree: FolderTree;
  activeId: string | null;
  onOpen: (folderId: string) => void;
  dragging: SpaceNodeView | null;
  onDropNode: (folderId: string) => void;
  trailing?: React.ReactNode;
}) {
  const entry = tree.entries[id];
  const open = tree.expanded.has(id);
  const folders = entry?.folders ?? null;
  // Unknown until loaded, so the chevron shows; once a folder is known to hold none, it goes.
  const leaf = folders !== null && folders.length === 0;
  const active = id === activeId;
  const target = useMoveTarget(id, dragging, onDropNode);

  return (
    <>
      <div
        {...target.props}
        className={cn(
          'flex items-center rounded-md transition-colors',
          active ? 'bg-orange-500 text-white' : 'hover:bg-surface-subtle',
          target.over && 'ring-2 ring-orange-400 ring-inset',
        )}
        style={{ paddingLeft: depth * 12 }}
      >
        <button
          type="button"
          onClick={() => !leaf && tree.toggle(id)}
          className={cn(
            'w-4 h-6 flex items-center justify-center flex-shrink-0',
            leaf ? 'opacity-0 cursor-default' : active ? 'text-orange-100' : 'text-ink-faint hover:text-ink',
          )}
          tabIndex={leaf ? -1 : 0}
          aria-label={leaf ? undefined : open ? `Collapse ${name}` : `Expand ${name}`}
        >
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button type="button" onClick={() => onOpen(id)} className="flex-1 flex items-center gap-1.5 py-1.5 min-w-0 text-left pr-1.5">
          {open && !leaf ? (
            <FolderOpen size={12} className={cn('flex-shrink-0', active ? 'text-orange-100' : 'text-ink-faint')} />
          ) : (
            <Folder size={12} className={cn('flex-shrink-0', active ? 'text-orange-100' : 'text-ink-faint')} />
          )}
          <span className={cn('text-xs truncate', active ? 'text-white font-medium' : 'text-ink')}>{name}</span>
          {trailing}
        </button>
      </div>
      {open && (
        <>
          {folders?.map((child) => (
            <TreeRow
              key={child.id}
              id={child.id}
              name={child.name}
              depth={depth + 1}
              tree={tree}
              activeId={activeId}
              onOpen={onOpen}
              dragging={dragging}
              onDropNode={onDropNode}
            />
          ))}
          {entry?.loading && folders === null && (
            <p className="text-[10px] text-ink-faint py-1" style={{ paddingLeft: (depth + 1) * 12 + 20 }}>Loading…</p>
          )}
          {entry?.error && (
            <p className="text-[10px] text-danger-text py-1 pr-2" style={{ paddingLeft: (depth + 1) * 12 + 20 }}>{entry.error}</p>
          )}
        </>
      )}
    </>
  );
}

/** The search box at the top of the pane: Enter opens the results, searching everything you can see. */
function SearchBox({ query, onSearch }: { query: string; onSearch: (q: string) => void }) {
  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.trim()) onSearch(draft.trim());
      }}
      className="relative"
    >
      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Search files" aria-label="Search files" className="pl-7 pr-2 py-1.5 text-xs" />
    </form>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="px-2 pb-1.5 text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">{children}</p>;
}

function NavEntry({
  icon: Icon, label, active, onClick, count,
}: { icon: typeof Trash2; label: string; active: boolean; onClick: () => void; count?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors',
        active ? 'bg-orange-500 text-white font-medium' : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
      )}
    >
      <Icon size={12} className={active ? 'text-orange-100' : 'text-ink-faint'} />
      <span className="flex-1 text-left">{label}</span>
      {count !== undefined && count > 0 && (
        <span className={cn('rounded-full px-1.5 text-[10px] font-semibold tabular-nums', active ? 'bg-white/25 text-white' : 'bg-orange-500 text-white')}>
          {count}
        </span>
      )}
    </button>
  );
}

function QuotaMeter({ used, quota }: { used: number; quota: number }) {
  const share = quota > 0 ? Math.min(1, used / quota) : 0;
  return (
    <div>
      <div className="h-1.5 rounded-sm bg-surface-muted overflow-hidden">
        <div className={cn('h-full', share >= 0.95 ? 'bg-danger-text' : share >= 0.8 ? 'bg-warning-text' : 'bg-accent')} style={{ width: `${share * 100}%` }} />
      </div>
      <p className="mt-1.5 text-[10px] text-ink-faint tabular-nums">
        {formatBytes(used)} of {formatBytes(quota)} used
        {/* Old versions and the trash count too — said here because a full space with few files is otherwise a mystery. */}
        <span className="block">Versions and trash count toward it.</span>
      </p>
    </div>
  );
}

/**
 * The left pane: your space as a tree, what others have shared with you, and the views that are about
 * your space as a whole (Share requests, Trash and Activity).
 *
 * Shared folders are grouped by owner and each opens at the folder you were given — the tree under it
 * is the same lazily loaded tree as your own, since a folder id is unique across spaces. Nothing above
 * a shared folder is ever shown, because the server never says what is there.
 */
export function SpaceNav({
  me, meError, shared, sharedError, tree, view, activeFolderId, onOpenFolder, onOpenView, onSearch, query, dragging, onDropNode, requestCount,
}: {
  me: SpaceSummary | null;
  meError: string | null;
  shared: SharedSpace[] | null;
  sharedError: string | null;
  tree: FolderTree;
  view: SpacesView;
  activeFolderId: string | null;
  onOpenFolder: (folderId: string) => void;
  onOpenView: (view: 'trash' | 'activity' | 'requests') => void;
  /** Share requests waiting for you; the entry shows the count. */
  requestCount: number;
  /** Opens the search results for these words. */
  onSearch: (q: string) => void;
  /** What the results open now are for, so the box keeps showing it. */
  query: string;
  dragging: SpaceNodeView | null;
  onDropNode: (folderId: string) => void;
}) {
  const activeId = view === 'folder' ? activeFolderId : null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-5">
        {me && <SearchBox query={query} onSearch={onSearch} />}
        <div>
          <SectionLabel>My space</SectionLabel>
          {meError ? (
            <p className="px-2 text-[11px] text-danger-text">{meError}</p>
          ) : !me ? (
            <p className="px-2 text-[11px] text-ink-faint">Loading…</p>
          ) : (
            <TreeRow
              id={me.rootFolderId}
              name="My space"
              depth={0}
              tree={tree}
              activeId={activeId}
              onOpen={onOpenFolder}
              dragging={dragging}
              onDropNode={onDropNode}
              trailing={<FolderLock size={11} className={cn('ml-auto flex-shrink-0', activeId === me.rootFolderId ? 'text-orange-100' : 'text-ink-faint')} />}
            />
          )}
        </div>

        <div>
          <SectionLabel>Shared with me</SectionLabel>
          {sharedError ? (
            <p className="px-2 text-[11px] text-danger-text">{sharedError}</p>
          ) : shared === null ? (
            <p className="px-2 text-[11px] text-ink-faint">Loading…</p>
          ) : shared.length === 0 ? (
            <p className="px-2 text-[11px] text-ink-faint leading-relaxed">Nothing yet. Folders other people share with you appear here.</p>
          ) : (
            <div className="space-y-3">
              {shared.map((space) => (
                <div key={space.handle}>
                  <p className="px-2 pb-1 flex items-center gap-1.5 min-w-0" title={space.ownerEmail}>
                    <Users size={11} className="text-ink-faint flex-shrink-0" />
                    <span className="text-[11px] font-medium text-ink-secondary truncate">{space.ownerName?.trim() || space.ownerEmail}</span>
                    <span className="text-[10px] text-ink-faint truncate">@{space.handle}</span>
                  </p>
                  {space.folders.map((folder) => (
                    <TreeRow
                      key={folder.id}
                      id={folder.id}
                      name={folder.name}
                      depth={0}
                      tree={tree}
                      activeId={activeId}
                      onOpen={onOpenFolder}
                      dragging={dragging}
                      onDropNode={onDropNode}
                      trailing={
                        <Badge
                          variant={folder.access === 'editor' ? 'info' : 'muted'}
                          className={cn('ml-auto flex-shrink-0 px-1.5 py-0 text-[9px]', activeId === folder.id && 'bg-white/25 text-white ring-0')}
                        >
                          {folder.access}
                        </Badge>
                      }
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {me && (
          <div className="space-y-0.5">
            <NavEntry icon={Inbox} label="Share requests" active={view === 'requests'} onClick={() => onOpenView('requests')} count={requestCount} />
            <NavEntry icon={Trash2} label="Trash" active={view === 'trash'} onClick={() => onOpenView('trash')} />
            <NavEntry icon={Activity} label="Activity" active={view === 'activity'} onClick={() => onOpenView('activity')} />
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-border px-4 py-3 space-y-2.5">
        {me && <QuotaMeter used={me.usedBytes} quota={me.quotaBytes} />}
        <p className="text-[10px] text-ink-faint leading-relaxed">
          Private from other users. System operators with infrastructure access can read stored files.
        </p>
      </div>
    </div>
  );
}
